"use client"

import { motion } from "framer-motion"
import { BookOpen, Users, Globe, Award, Video, CheckCircle2 } from "lucide-react"

export function AboutAcademySection() {
  return (
    <section className="relative py-16 sm:py-20 lg:py-24 bg-[rgb(252,249,242)] overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #245E51 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#A8FF51]/20 border border-[#A8FF51]/30 mb-6">
            <span className="text-sm font-semibold text-[#245E51]">About Dhikr Institute</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Where Authentic Knowledge
            <br />
            <span className="text-[#245E51]">Meets Modern Learning</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Combining Egyptian certified Quran teachers with UK professional management 
            to deliver world-class Islamic education online
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Video Section */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <video
                className="w-full h-full aspect-video object-cover"
                controls
                preload="metadata"
                poster="/quran-memorization.jpg"
              >
                <source src="/videos/2025-10-21 17.56.18.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </motion.div>

          {/* Content Section */}
          <div className="space-y-8">
            {/* Feature 1 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex gap-4"
            >
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-[#245E51]/10 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-[#245E51]" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Egyptian Certified Teachers
                </h3>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line line-clamp-2">
                  Our teachers hold Ijazah in Quran recitation and have deep knowledge of Islamic sciences.
                  They bring authentic scholarship and traditional teaching methods to every lesson.
                </p>
              </div>
            </motion.div>

            {/* Feature 2 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex gap-4"
            >
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-[#A8FF51]/20 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-[#245E51]" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  UK Professional Management
                </h3>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line line-clamp-2">
                  Our UK-based management team ensures quality, consistency, and excellent customer service.
                  We combine traditional knowledge with modern educational standards.
                </p>
              </div>
            </motion.div>

            {/* Feature 3 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex gap-4"
            >
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Inclusive for All Ages & Genders
                </h3>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line line-clamp-2">
                  We offer programs suitable for children, teens, and adults.
                  Our courses welcome both men and women from all backgrounds and countries worldwide.
                </p>
              </div>
            </motion.div>
          </div>

        </div>

        {/* Removed bottom stats cards as requested */}
      </div>
    </section>
  )
}
